import React from "react"
import { useQuery } from "@tanstack/react-query"
import { DollarSign, ShoppingBag, Users, Truck, ArrowUpRight, ArrowDownRight, Activity, Clock, AlertCircle } from "lucide-react"
import { dashboardAPI } from "../services/api"
import { useLanguage } from "../contexts/LanguageContext"
import PageHeader from "../components/ui/dashboard/PageHeader"
import StatCard from "../components/ui/dashboard/StatCard"
import DataCard from "../components/ui/dashboard/DataCard"
import LoadingSpinner from "../components/ui/dashboard/LoadingSpinner"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts'
import { Box, Card, Flex, Text, Grid, Badge, Avatar, Tabs } from "@radix-ui/themes"

const DashboardPage: React.FC = () => {
  const { t } = useLanguage()

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: dashboardAPI.getStats,
  })

  const { data: recentOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ["recent-orders"],
    queryFn: dashboardAPI.getRecentOrders,
  })

  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ["dashboard-analytics"],
    queryFn: () => dashboardAPI.getAnalytics({ period: "7d" }),
  })

  // Format backend analytics into unified chart data
  const chartData = React.useMemo(() => {
    if (!analytics?.data) return []

    const { orders = [], revenue = [], users = [] } = analytics.data

    // Create a map of all unique dates natively aggregated from MongoDB
    const dateMap = new Map()

    orders.forEach((o: any) => {
      dateMap.set(o._id, { name: o._id, orders: o.total || 0, revenue: 0, newUsers: 0 })
    })

    revenue.forEach((r: any) => {
      if (!dateMap.has(r._id)) dateMap.set(r._id, { name: r._id, orders: 0, revenue: 0, newUsers: 0 })
      dateMap.get(r._id).revenue = r.revenue || 0
    })

    users.forEach((u: any) => {
      if (!dateMap.has(u._id)) dateMap.set(u._id, { name: u._id, orders: 0, revenue: 0, newUsers: 0 })
      dateMap.get(u._id).newUsers = u.newUsers || 0
    })

    // Sort by date ascending
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.name).getTime() - new Date(b.name).getTime())
  }, [analytics])

  const statCards = [
    {
      title: t("totalRevenue"),
      value: `SDG ${stats?.data?.totalRevenue?.toLocaleString() || "0"}`,
      icon: DollarSign,
      color: "bg-green-500",
      change: "+12.5%",
      changeType: "positive" as const,
      subtext: "vs last month"
    },
    {
      title: t("totalOrders"),
      value: stats?.data?.totalOrders?.toLocaleString() || "0",
      icon: ShoppingBag,
      color: "bg-blue-500",
      change: "+8.2%",
      changeType: "positive" as const,
      subtext: "vs last month"
    },
    {
      title: t("activeUsers"),
      value: stats?.data?.activeUsers?.toLocaleString() || "0",
      icon: Users,
      color: "bg-purple-500",
      change: "+15.3%",
      changeType: "positive" as const,
      subtext: "vs last month"
    },
    {
      title: t("activeCaptains"),
      value: stats?.data?.activeCaptains?.toLocaleString() || "0",
      icon: Truck,
      color: "bg-orange-500",
      change: "-2.1%",
      changeType: "negative" as const,
      subtext: "vs last month"
    },
  ]

  if (statsLoading) {
    return <LoadingSpinner size="3" text={t("loadingDashboardData")} />
  }

  return (
    <div className="space-y-6">
      <Flex justify="between" align="center" wrap="wrap" gap="4">
        <PageHeader
          title={t("dashboard")}
          subtitle={t("welcomeBack")}
        />
        <Flex gap="3">
          <Badge color="blue" variant="soft" size="2">
            <Clock className="w-3 h-3 mr-1" />
            Last updated: {new Date().toLocaleTimeString()}
          </Badge>
        </Flex>
      </Flex>

      <Tabs.Root defaultValue="overview">
        <Tabs.List>
          <Tabs.Trigger value="overview">{t("overview")}</Tabs.Trigger>
          <Tabs.Trigger value="analytics">{t("analytics")}</Tabs.Trigger>
          <Tabs.Trigger value="realtime">{t("realTime")}</Tabs.Trigger>
        </Tabs.List>

        <Box pt="4">
          <Tabs.Content value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statCards.map((stat, index) => (
                <StatCard
                  key={index}
                  title={stat.title}
                  value={stat.value}
                  icon={stat.icon}
                  color={stat.color}
                  change={stat.change}
                  changeType={stat.changeType}
                  loading={statsLoading}
                />
              ))}
            </div>

            {/* Charts Section */}
            <Grid columns={{ initial: '1', md: '2' }} gap="6">
              <Card>
                <Flex justify="between" align="center" mb="4">
                  <Text size="3" weight="bold">Revenue Overview</Text>
                  <Badge color="green" variant="soft">+12.5%</Badge>
                </Flex>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--gray-5)" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--gray-11)', fontSize: 12 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--gray-11)', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--color-panel-solid)', borderRadius: '8px', border: 'none', boxShadow: 'var(--shadow-3)' }}
                        itemStyle={{ color: 'var(--gray-12)' }}
                      />
                      <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card>
                <Flex justify="between" align="center" mb="4">
                  <Text size="3" weight="bold">Order Volume</Text>
                  <Badge color="blue" variant="soft">+8.2%</Badge>
                </Flex>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--gray-5)" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'var(--gray-11)', fontSize: 12 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fill: 'var(--gray-11)', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'var(--color-panel-solid)', borderRadius: '8px', border: 'none', boxShadow: 'var(--shadow-3)' }}
                        itemStyle={{ color: 'var(--gray-12)' }}
                        cursor={{ fill: 'var(--gray-3)' }}
                      />
                      <Bar dataKey="orders" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Grid>

            {/* Recent Orders & Activity */}
            <Grid columns={{ initial: '1', lg: '3' }} gap="6">
              <Box className="lg:col-span-2">
                <DataCard
                  title={t("recentOrders")}
                  loading={ordersLoading}
                  isEmpty={!recentOrders?.data?.orders?.length}
                  emptyMessage={t("noRecentOrders")}
                >
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {recentOrders?.data?.orders?.slice(0, 5).map((order: any) => (
                      <div key={order._id} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <Flex gap="3" align="center">
                          <Avatar
                            fallback={order.restaurant?.name?.charAt(0) || 'R'}
                            color="indigo"
                            size="2"
                            radius="full"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white text-sm">
                              {t("orderNumber").replace("#{number}", order.orderNumber)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{order.restaurant?.name}</p>
                          </div>
                        </Flex>
                        <div className="text-right">
                          <p className="font-medium text-gray-900 dark:text-white text-sm">SDG {order.pricing?.total}</p>
                          <Badge
                            color={
                              order.status === 'delivered' ? 'green' :
                                order.status === 'pending' ? 'yellow' :
                                  'blue'
                            }
                            variant="soft"
                            size="1"
                          >
                            {order.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </DataCard>
              </Box>

              <Card>
                <Flex justify="between" align="center" mb="4">
                  <Text size="3" weight="bold">System Activity</Text>
                  <Activity className="w-4 h-4 text-gray-500" />
                </Flex>
                <Flex direction="column" gap="4">
                  {ordersLoading ? (
                    <Text size="2" color="gray">{t("loading")}...</Text>
                  ) : recentOrders?.data?.orders?.length > 0 ? (
                    recentOrders?.data?.orders.slice(0, 5).map((order: any, i: number) => (
                      <Flex key={`activity-${order._id}-${i}`} gap="3" align="start">
                        <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${order.status === 'delivered' ? 'bg-green-500' :
                            order.status === 'cancelled' ? 'bg-red-500' :
                              'bg-blue-500'
                          }`} />
                        <div>
                          <Text size="2" className="block text-gray-900 dark:text-gray-100">
                            {order.status === 'delivered'
                              ? `Order placed by ${order.customer?.username || 'Guest'} was delivered`
                              : order.status === 'cancelled'
                                ? `Order at ${order.restaurant?.name || 'Restaurant'} was cancelled`
                                : `New order placed at ${order.restaurant?.name || 'Restaurant'}`
                            }
                          </Text>
                          <Text size="1" color="gray">
                            {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Text>
                        </div>
                      </Flex>
                    ))
                  ) : (
                    <Text size="2" color="gray">No recent activity</Text>
                  )}
                </Flex>
              </Card>
            </Grid>
          </Tabs.Content>

          <Tabs.Content value="analytics">
            <Card>
              <Flex justify="center" align="center" style={{ height: '300px' }}>
                <Text color="gray">Advanced analytics coming soon...</Text>
              </Flex>
            </Card>
          </Tabs.Content>

          <Tabs.Content value="realtime">
            <Card>
              <Flex justify="center" align="center" style={{ height: '300px' }}>
                <Text color="gray">Live map view coming soon...</Text>
              </Flex>
            </Card>
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </div>
  )
}

export default DashboardPage
